import React, { useMemo, useState } from 'react'
import { $TOWNS } from '@components/$TOWNS/$TOWNS'
import { Panel } from '@components/Panel/Panel'
import { Box, Divider, Grid, Heading, Paragraph, Stack, Text, Toggle } from '@ui'
import { ethers } from 'ethers'
import { AnimatePresence } from 'framer-motion'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useFetchOperatorsQuery } from 'hooks/useOperatorData'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { useStakingRewardsQuery } from 'use-towns-client'
import { env } from 'utils/environment'

import { SpaceDeposits, useStakingDeposits } from './hooks/useStakingData'
import { ClaimMyRewardsButton } from './ClaimMyRewardsButton'

const DEBUG = env.DEV && false

const calculateRewardWithYield = (currentReward: bigint, networkApy: number) => {
    const scale = 1_000_000
    const aprScaled = BigInt(Math.round(networkApy * scale))
    const yearlyYieldAmount = (currentReward * aprScaled) / BigInt(scale)
    return {
        totalAmount: currentReward + yearlyYieldAmount,
        yearlyYieldAmount,
    }
}

export const ClaimRewardsPanel = () => {
    const { openPanel } = usePanelActions()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()

    const {
        data: currentReward,
        isLoading: isRewardLoading,
        error: rewardError,
    } = useStakingRewardsQuery({
        beneficiary: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    const {
        data: { groupedDeposits, totalStaked },
        isLoading: isDepositsLoading,
        error: depositsError,
    } = useStakingDeposits({
        owner: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    const hasRewards = useMemo(() => {
        if (!currentReward) {
            return false
        }
        return currentReward.gt(0)
    }, [currentReward])

    const totalDeposits = groupedDeposits.reduce((sum, space) => sum + space.deposits.length, 0)

    const isLoading = isRewardLoading || isDepositsLoading
    const error = rewardError || depositsError

    const [isStakeEnabled, setIsStakeEnabled] = useState(true)

    const { data: operatorsData } = useFetchOperatorsQuery()
    const networkApy = operatorsData?.networkEstimatedApy ?? 0
    const yieldPercentageDisplay = `${(networkApy * 100).toFixed(1)}%`
    const { totalAmount, yearlyYieldAmount } = isStakeEnabled
        ? calculateRewardWithYield(currentReward?.toBigInt() ?? 0n, networkApy)
        : { totalAmount: currentReward?.toBigInt() ?? 0n, yearlyYieldAmount: 0n }

    return (
        <Panel label="Claim Rewards">
            <Stack grow gap="lg" color="default">
                {isLoading ? (
                    <Text size="sm" color="gray2" textAlign="center">
                        Loading rewards...
                    </Text>
                ) : error ? (
                    <Text size="sm" color="error" textAlign="center">
                        Error loading rewards:{' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </Text>
                ) : !hasRewards ? (
                    <Stack
                        background="negativeSubtle"
                        border="level3"
                        padding="lg"
                        borderRadius="md"
                    >
                        <Paragraph color="gray2">
                            You don&apos;t have any rewards to claim at the moment. Stake $TOWNS
                            tokens to earn rewards!
                        </Paragraph>
                    </Stack>
                ) : (
                    <Stack gap="lg">
                        <Box padding="lg" borderRadius="sm" background="level2">
                            <Stack gap="lg">
                                <Stack centerContent gap>
                                    <TownsShinySvg />
                                    <Heading level={3}>Claim Rewards</Heading>
                                </Stack>
                                <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                                    <Stack gap>
                                        <Paragraph strong size="lg">
                                            Stake & Earn {yieldPercentageDisplay} Yield
                                        </Paragraph>
                                        <Text size="sm" color="gray2">
                                            30-day withdrawal period applies.
                                        </Text>
                                    </Stack>
                                    <Toggle
                                        toggled={isStakeEnabled}
                                        onToggle={(checked) => setIsStakeEnabled(checked)}
                                    />
                                </Stack>

                                <Divider />

                                <Grid columns={2}>
                                    <Text>Base</Text>
                                    <Stack gap alignItems="end">
                                        <Text strong>
                                            <$TOWNS currency="usd" value={currentReward ?? 0n} />
                                        </Text>
                                        <Text size="sm" color="gray2">
                                            <$TOWNS value={currentReward ?? 0n} />
                                        </Text>
                                    </Stack>
                                </Grid>

                                <AnimatePresence initial={false}>
                                    <Stack>
                                        <Grid
                                            columns={2}
                                            color={isStakeEnabled ? 'positive' : 'gray2'}
                                        >
                                            <Text>Yearly Return</Text>
                                            <Stack gap alignItems="end">
                                                <Text strong>
                                                    +
                                                    <$TOWNS
                                                        currency="usd"
                                                        value={yearlyYieldAmount}
                                                    />
                                                </Text>
                                                <Text size="sm" color="gray2">
                                                    +
                                                    <$TOWNS value={yearlyYieldAmount} />
                                                </Text>
                                            </Stack>
                                        </Grid>
                                    </Stack>
                                </AnimatePresence>

                                <Divider />

                                <Grid columns={2}>
                                    <Text strong>Total</Text>
                                    <Stack gap alignItems="end">
                                        <Text strong color="default">
                                            <$TOWNS currency="usd" value={totalAmount} />
                                        </Text>
                                        <Text size="sm" color="gray2">
                                            <$TOWNS value={totalAmount} />
                                        </Text>
                                    </Stack>
                                </Grid>
                            </Stack>
                        </Box>

                        {DEBUG && (
                            <DebugBox
                                totalStaked={totalStaked}
                                userDeposits={groupedDeposits}
                                totalDeposits={totalDeposits}
                            />
                        )}
                    </Stack>
                )}
                <Stack grow gap="paragraph" justifyContent="end">
                    <ClaimMyRewardsButton
                        currentReward={currentReward}
                        label={isStakeEnabled ? 'Continue to Stake' : 'Claim'}
                        action={isStakeEnabled ? 'redirectToStake' : 'claim'}
                        onSuccess={() => {
                            if (isStakeEnabled) {
                                openPanel(CHANNEL_INFO_PARAMS.STAKE_TO_TOWN, {
                                    stackId: CHANNEL_INFO_PARAMS.CLAIM_REWARDS,
                                })
                            } else {
                                openPanel(CHANNEL_INFO_PARAMS.TRADING_WALLET, {
                                    data: JSON.stringify({ activeTab: 'staked' }),
                                })
                            }
                        }}
                    />
                </Stack>
            </Stack>
        </Panel>
    )
}

// removed Desired prototype implementation
const DebugBox = (props: {
    totalStaked: ethers.BigNumber
    userDeposits: SpaceDeposits[]
    totalDeposits: number
}) => {
    const { totalStaked, userDeposits, totalDeposits } = props

    return (
        <Box gap padding borderRadius="sm" background="peachSubtle" border="level3">
            <Paragraph strong>Info</Paragraph>
            <Stack gap="sm">
                <Paragraph size="sm" color="gray2">
                    Total Staked: <$TOWNS value={totalStaked} />
                </Paragraph>
                <Paragraph size="sm" color="gray2">
                    Staking in {userDeposits.length} space
                    {userDeposits.length !== 1 ? 's' : ''}
                </Paragraph>
                <Paragraph size="sm" color="gray2">
                    Total deposits: {totalDeposits}
                </Paragraph>
            </Stack>
        </Box>
    )
}

const TownsShinySvg = () => (
    <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="35" cy="35" r="35" fill="url(#paint0_linear_37596_170972)" />
        <path
            d="M50.0562 14.9922C53.0488 14.9922 55.475 17.4185 55.4751 20.4111V25.4863C55.4751 25.7669 55.4537 26.0425 55.4126 26.3115L55.4341 26.3057V26.5762C55.4339 29.4215 53.1271 31.7284 50.2817 31.7285C49.2963 31.7285 48.4976 32.5273 48.4976 33.5127V45.8408C48.4976 47.0696 48.0583 48.258 47.2593 49.1914L43.978 53.0244C43.9131 53.1058 43.8459 53.1856 43.7778 53.2646C42.7539 54.4528 41.4986 55.0565 40.1831 55.0566H28.8618C27.5462 55.0566 26.2902 54.4529 25.2661 53.2646C24.2077 52.0363 24.0396 50.4994 24.0396 49.2979V47.1836C24.0393 44.8836 22.1746 43.0195 19.8745 43.0195C18.77 43.0195 17.7112 42.5798 16.9302 41.7988L15.8521 40.7217C15.0024 39.872 14.5249 38.7192 14.5249 37.5176V30.8623C14.5249 29.8262 14.8797 28.8207 15.5308 28.0146C16.1807 27.2099 23.9962 16.9252 24.0396 16.8682C24.6832 16.0713 25.5647 15.5302 26.5298 15.3037C27.097 15.1022 27.7079 14.9922 28.3442 14.9922H50.0562ZM28.3442 18.5996C27.3434 18.5996 26.5318 19.4113 26.5317 20.4121V25.4873C26.532 26.488 27.3435 27.2988 28.3442 27.2988H33.0562C34.057 27.2988 34.8685 28.1105 34.8687 29.1113V42.1602C34.8687 43.1611 35.6802 43.9727 36.6812 43.9727H41.7192C42.7202 43.9727 43.5317 43.1611 43.5317 42.1602V29.1113C43.5318 28.1105 44.3434 27.2988 45.3442 27.2988H50.0562C51.0569 27.2987 51.8684 26.488 51.8687 25.4873V20.4121C51.8685 19.4113 51.0569 18.5997 50.0562 18.5996H28.3442Z"
            fill="white"
        />
        <path
            d="M9.30729 5.37539C9.55639 4.87487 10.4436 4.87487 10.6927 5.37539C11.2169 6.42871 11.9694 7.78222 12.7022 8.54015C13.4626 9.32658 14.6937 9.97999 15.5997 10.3926C16.0802 10.6116 16.1437 11.2257 15.7053 11.5025C14.7914 12.079 13.4928 12.9627 12.7022 13.7803C11.9542 14.5539 11.185 15.7608 10.6597 16.6607C10.3954 17.1131 9.60459 17.1131 9.34028 16.6607C8.81497 15.7608 8.04581 14.5539 7.29778 13.7803C6.50725 12.9627 5.20864 12.079 4.29472 11.5025C3.85626 11.2257 3.91984 10.6116 4.40027 10.3926C5.30633 9.97999 6.53738 9.32658 7.29778 8.54015C8.03063 7.78222 8.78308 6.42871 9.30729 5.37539Z"
            fill="#CCA5FE"
        />
        <path
            d="M57.9805 57.3128C58.1673 56.8957 58.8327 56.8957 59.0195 57.3128C59.4127 58.1906 59.977 59.3185 60.5267 59.9501C61.097 60.6055 62.0203 61.15 62.6998 61.4938C63.0601 61.6763 63.1078 62.1881 62.779 62.4187C62.0935 62.8992 61.1196 63.6356 60.5267 64.3169C59.9656 64.9616 59.3888 65.9673 58.9948 66.7173C58.7966 67.0942 58.2034 67.0942 58.0052 66.7173C57.6112 65.9673 57.0344 64.9616 56.4733 64.3169C55.8804 63.6356 54.9065 62.8992 54.221 62.4187C53.8922 62.1881 53.9399 61.6763 54.3002 61.4938C54.9798 61.15 55.903 60.6055 56.4733 59.9501C57.023 59.3185 57.5873 58.1906 57.9805 57.3128Z"
            fill="#CCA5FE"
        />
        <path
            d="M17.6536 1.1877C17.7782 0.937435 18.2218 0.937435 18.3464 1.1877C18.6085 1.71436 18.9847 2.39111 19.3511 2.77008C19.7313 3.16329 20.3468 3.48999 20.7999 3.69631C21.0401 3.80579 21.0719 4.11287 20.8526 4.25125C20.3957 4.53951 19.7464 4.98134 19.3511 5.39013C18.9771 5.77695 18.5925 6.38039 18.3299 6.83036C18.1977 7.05655 17.8023 7.05655 17.6701 6.83036C17.4075 6.38039 17.0229 5.77695 16.6489 5.39013C16.2536 4.98134 15.6043 4.53951 15.1474 4.25125C14.9281 4.11287 14.9599 3.80579 15.2001 3.69631C15.6532 3.48999 16.2687 3.16329 16.6489 2.77008C17.0153 2.39111 17.3915 1.71436 17.6536 1.1877Z"
            fill="#CCA5FE"
        />
        <defs>
            <linearGradient
                id="paint0_linear_37596_170972"
                x1="35"
                y1="0"
                x2="35"
                y2="70"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor="#8237E2" />
                <stop offset="1" stopColor="#7A2BDF" />
            </linearGradient>
        </defs>
    </svg>
)
